import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../api/client';

interface BatchSummary {
  batch_id: string;
  file_count: number;
  total_bytes: number;
  last_upload_at: string | null;
  has_metadata_csv: boolean;
  last_validation_state: string | null;
  last_validated_at: string | null;
}

export function BatchesPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { data, isLoading, error } = useQuery<BatchSummary[], ApiError>({
    queryKey: ['batches'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<BatchSummary[]>('/api/v1/batches', { token });
    },
    retry: false,
  });

  if (isLoading) return <div>Loading…</div>;
  if (error) {
    return (
      <div className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h1 className="text-xl font-semibold">Couldn't load batches</h1>
        <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">{error.message}</p>
      </div>
    );
  }

  const batches = data ?? [];

  return (
    <div>
      <h1 className="text-2xl font-semibold">Your batches</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        Each <code className="font-mono">batch-…</code> folder you've uploaded. After you
        click Submit Upload on the upload page, a DFX super-admin reviews and validates
        your delivery and contacts you with the results.
      </p>

      {batches.length === 0 ? (
        <div className="mt-8 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6 text-sm text-[color:var(--color-text-secondary)]">
          No batches yet.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded border border-[color:var(--color-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-bg-card)] text-left text-xs uppercase tracking-wider text-[color:var(--color-text-muted)]">
              <tr>
                <th className="px-4 py-2">Batch</th>
                <th className="px-4 py-2">Files</th>
                <th className="px-4 py-2">Last upload</th>
                <th className="px-4 py-2">metadata.csv</th>
                <th className="px-4 py-2">Last validation</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id} className="border-t border-[color:var(--color-border)]">
                  <td className="px-4 py-3 font-mono">{b.batch_id}</td>
                  <td className="px-4 py-3">{b.file_count}</td>
                  <td className="px-4 py-3 text-[color:var(--color-text-secondary)]">
                    {b.last_upload_at ? new Date(b.last_upload_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {b.has_metadata_csv ? (
                      <span className="text-[color:var(--color-success,#4ade80)]">✓ uploaded</span>
                    ) : (
                      <span className="text-[color:var(--color-warning)]">— not yet</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {b.last_validated_at ? (
                      <ValidationBadge state={b.last_validation_state} at={b.last_validated_at} />
                    ) : (
                      <span className="text-[color:var(--color-text-secondary)]">awaiting DFX</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ValidationBadge({ state, at }: { state: string | null; at: string }) {
  const label = state ?? 'unknown';
  const color =
    state === 'passed'
      ? 'var(--color-success, #4ade80)'
      : state === 'failed'
      ? 'var(--color-warning)'
      : 'var(--color-text-secondary)';
  return (
    <span style={{ color }} title={new Date(at).toLocaleString()}>
      {label}
    </span>
  );
}
